/**
 * Audit log presentation helpers.
 *
 * AuditLog rows come from two different write paths and use two
 * different fields to encode "what happened":
 *
 *   * The top-level ``action`` field uses the narrow legacy enum
 *     ``"created" | "modified" | "deleted"`` — set by older write paths
 *     (none currently active in this repo) or inferred from
 *     before/after-value presence.
 *   * The rich action key (``"publish_version"``, ``"import_questions"``,
 *     ``"archive_baseline"``, …) is stored inside ``after_value.action``
 *     by the ``AuditTrailManager.log_event`` write path. This is the
 *     primary signal for everything written by the orchestrators and
 *     the question importer.
 *
 * This module is the single home for "given an audit row, what badge
 * label and title text does the UI show?" — both ``AuditLogPage`` (super
 * admin) and ``VersionDetailPage`` (audit tab) read from it so the two
 * views never disagree.
 */

export interface AuditLogEntry {
  id: string;
  entity_type: string;
  entity_id?: string;
  entity_name?: string;
  /** Legacy top-level enum. New write paths leave this unset and put
   *  the rich action key in ``after_value.action`` instead. */
  action?: "created" | "modified" | "deleted";
  before_value?: Record<string, unknown> | null;
  after_value?: Record<string, unknown> | null;
  version_id?: string;
  changed_by: string;
  changed_at: string;
  /** Legacy field-diff payload from ``VersionAuditEntry``-shaped rows
   *  (older write paths populated this instead of ``before_value`` /
   *  ``after_value``). New rows leave it unset. */
  fields?: Array<{ field: string; old_value: string; new_value: string }>;
}

// ---------------------------------------------------------------------------
// Rich action -> presentation
// ---------------------------------------------------------------------------

interface ActionPresentation {
  /** Short label shown in the colored badge column. Capped at ~10 chars
   *  so it fits in the existing ``w-18`` (4.5rem) badge slot. */
  label: string;
  /** Tailwind classes for the badge background + text. Aligned with the
   *  XYDEN palette: green for completed/positive, amber for in-progress
   *  or edits, red for destructive, blue for neutral signal, gray for
   *  ended/archived. */
  classes: string;
  /** Verb phrase used by ``formatAuditTitle`` to build the headline
   *  (e.g. "Published" -> "Published version: ..."). */
  verb: string;
}

const GREEN = "bg-green-100 text-green-700 border-green-300";
const AMBER = "bg-amber-100 text-amber-700 border-amber-300";
const RED = "bg-red-100 text-red-600 border-red-300";
const BLUE = "bg-blue-100 text-blue-700 border-blue-300";
const GRAY = "bg-brand-steel/15 text-brand-steel border-brand-steel/30";

const ACTION_PRESENTATION: Record<string, ActionPresentation> = {
  // Legacy enum — kept so any pre-existing rows still render correctly.
  created: { label: "Created", classes: GREEN, verb: "Created" },
  modified: { label: "Modified", classes: AMBER, verb: "Updated" },
  deleted: { label: "Deleted", classes: RED, verb: "Deleted" },

  // Version lifecycle (orchestrators.py — content_publish_orchestrator)
  publish_version: { label: "Published", classes: GREEN, verb: "Published" },
  archive_version: { label: "Archived", classes: GRAY, verb: "Archived" },
  clone_to_draft: { label: "Cloned", classes: BLUE, verb: "Cloned" },
  edit_version_content: { label: "Modified", classes: AMBER, verb: "Edited" },

  // Question bank (content.py — import endpoint)
  import_questions: { label: "Imported", classes: BLUE, verb: "Imported" },

  // Assessment lifecycle (orchestrators.py — assessment_orchestrator)
  begin_assessment: { label: "Started", classes: BLUE, verb: "Started" },
  begin_aq: { label: "Started", classes: BLUE, verb: "Started Assessment Questionnaire for" },
  submit_tp_allocation: { label: "Submitted", classes: BLUE, verb: "Submitted Time Prioritization for" },
  complete_aq: { label: "Completed", classes: GREEN, verb: "Completed Assessment Questionnaire for" },
  generate_results: { label: "Results", classes: GREEN, verb: "Generated results for" },

  // Test lifecycle (orchestrators.py — test_lifecycle_orchestrator)
  transition_to_in_progress: { label: "Launched", classes: AMBER, verb: "Set in progress:" },
  create_and_launch: { label: "Launched", classes: BLUE, verb: "Launched" },
  complete_test: { label: "Completed", classes: GREEN, verb: "Completed" },
  close_test: { label: "Closed", classes: GRAY, verb: "Closed" },

  // Narrative pipeline (orchestrators.py — narrative_orchestrator)
  generate_narrative: { label: "Generated", classes: GREEN, verb: "Generated narrative:" },
  batch_generate: { label: "Batched", classes: GREEN, verb: "Batch-generated narratives for" },
  retry_failed: { label: "Retried", classes: AMBER, verb: "Retried narrative:" },

  // Baseline (orchestrators.py — baseline_orchestrator)
  create_baseline_from_assessment: { label: "Baselined", classes: GREEN, verb: "Created baseline:" },
  archive_baseline: { label: "Archived", classes: GRAY, verb: "Archived baseline:" },

  // Results (orchestrators.py — results_orchestrator)
  assemble_results: { label: "Assembled", classes: GREEN, verb: "Assembled results for" },
  get_comparative_results: { label: "Compared", classes: BLUE, verb: "Comparison run for" },
};

const FALLBACK_PRESENTATION: ActionPresentation = {
  label: "Event",
  classes: GRAY,
  verb: "Logged",
};

/** Resolve the rich action key for an entry — prefers the new
 *  ``after_value.action`` slot, falls back to the legacy top-level
 *  ``action`` field, and finally infers from before/after presence. */
export function getRichAction(entry: AuditLogEntry): string {
  const inAfter = entry.after_value?.action;
  if (typeof inAfter === "string" && inAfter.length > 0) return inAfter;
  if (entry.action) return entry.action;
  if (!entry.before_value) return "created";
  if (!entry.after_value) return "deleted";
  return "modified";
}

/** Badge label + Tailwind classes for the colored chip column. */
export function getActionPresentation(entry: AuditLogEntry): ActionPresentation {
  const action = getRichAction(entry);
  return ACTION_PRESENTATION[action] ?? FALLBACK_PRESENTATION;
}

/** De-duplicated action options for the audit-log filter Combobox.
 *
 *  Several rich-action keys share one display label (``begin_assessment``
 *  and ``begin_aq`` both render as "Started"; ``modified`` and
 *  ``edit_version_content`` both render as "Modified"). Selecting a label
 *  must therefore narrow to *every* key behind it, so each option's value is
 *  the comma-joined set of keys — the backend splits on comma and matches
 *  ``getRichAction(row)`` against the set. Options are sorted by label so the
 *  dropdown reads predictably. */
export function getActionFilterOptions(): Array<{ value: string; label: string }> {
  const keysByLabel = new Map<string, string[]>();
  for (const [key, pres] of Object.entries(ACTION_PRESENTATION)) {
    const keys = keysByLabel.get(pres.label) ?? [];
    keys.push(key);
    keysByLabel.set(pres.label, keys);
  }
  return Array.from(keysByLabel.entries())
    .map(([label, keys]) => ({ value: keys.join(","), label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// Title formatting
// ---------------------------------------------------------------------------

/** Best-effort entity name resolution. Honors any explicit fallback the
 *  caller passes (e.g. the loaded Version's name on VersionDetailPage),
 *  then ``entity_name`` from the row, then the version name embedded by
 *  the importer in ``after_value``, then ``entity_id`` as a last resort. */
function resolveEntityName(
  entry: AuditLogEntry,
  fallbackName?: string,
): string {
  if (fallbackName) return fallbackName;
  if (entry.entity_name) return entry.entity_name;
  const fromAfter = entry.after_value?.version_name;
  if (typeof fromAfter === "string" && fromAfter.length > 0) return fromAfter;
  return entry.entity_id ?? entry.entity_type;
}

/** Human-readable title for one audit row.
 *
 * Special-cases the bulk import event: its ``after_value.summary``
 * already reads like a complete sentence ("Bulk imported 9 questions —
 * 3 orphan"), so we use it verbatim instead of slotting it into the
 * generic "{Verb} {entity_type}: {name}" template.
 *
 * Pass ``versionName`` when the caller already has the version loaded
 * (e.g. ``VersionDetailPage``) — saves the row from having to embed it. */
export function formatAuditTitle(
  entry: AuditLogEntry,
  versionName?: string,
): string {
  const action = getRichAction(entry);

  // Importer events come with a pre-built summary line — use it verbatim
  // so the orphan-count and replace-count never get lost in templating.
  if (action === "import_questions") {
    const summary = entry.after_value?.summary;
    if (typeof summary === "string" && summary.length > 0) return summary;
  }

  const { verb } = getActionPresentation(entry);
  const type = entry.entity_type.toLowerCase();
  const name = resolveEntityName(entry, versionName);

  // For verbs that already encode "for" or ":" (Submitted TP for / Created
  // baseline:), don't double up on punctuation — slot the name directly.
  if (verb.endsWith(":") || verb.endsWith("for")) return `${verb} ${name}`;
  return `${verb} ${type}: ${name}`;
}

// ---------------------------------------------------------------------------
// Diff summary (modified / edit_version_content rows)
// ---------------------------------------------------------------------------

/** Short "<field> changed" / "<field>, <field> changed" line, only
 *  generated when before+after dicts are both present and a real diff
 *  exists. Returns null when there's nothing to say — caller should
 *  omit the trailing separator. */
export function formatChangeSummary(entry: AuditLogEntry): string | null {
  const before = entry.before_value;
  const after = entry.after_value;
  if (!before || !after) return null;
  const changed = Object.keys(after).filter(
    (k) => k !== "action" && JSON.stringify(before[k]) !== JSON.stringify(after[k]),
  );
  if (changed.length === 0) return null;
  if (changed.length === 1) return `${changed[0]} changed`;
  return `${changed.join(", ")} changed`;
}

// ---------------------------------------------------------------------------
// Time formatting (shared so both pages render times the same way)
// ---------------------------------------------------------------------------

export function formatRelativeTime(isoDate: string | null | undefined): string {
  // Safety guard: missing or unparseable input renders an em dash, never
  // "Invalid Date" / a 1970 epoch fallback.
  if (!isoDate) return "—";
  const time = new Date(isoDate).getTime();
  if (Number.isNaN(time)) return "—";

  const diff = Date.now() - time;
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"} ago`;

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return plural(minutes, "min");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, "hr");
  const days = Math.floor(hours / 24);
  if (days < 7) return plural(days, "day");
  if (days < 30) return plural(Math.floor(days / 7), "week");
  if (days < 365) return plural(Math.floor(days / 30), "month");
  // A year or older: show the absolute date.
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Entity-type -> human category label (where in the UI this thing lives)
// ---------------------------------------------------------------------------

const CATEGORY_MAP: Record<string, string> = {
  Activity: "Activities",
  Bucket: "Buckets",
  Faculty: "Competency Library",
  Behaviour: "Competency Library",
  Technique: "Competency Library",
  ScoringRule: "Scoring Rules",
  ScoringRuleSet: "Scoring Rules",
  TriadMapping: "Triad Matrix",
  ActivityMaturityMap: "Activity Maturity",
  Version: "Version Config",
  Question: "Question Bank",
  AuditLog: "Audit",
  Assessment: "Assessments",
  Test: "Tests",
  Narrative: "Narratives",
  Baseline: "Baselines",
  Results: "Results",
};

export function getCategoryLabel(entityType: string): string {
  return CATEGORY_MAP[entityType] ?? entityType;
}
